<script setup lang="ts">
import { stripBlockTags } from '@/composables/helpers/html';

interface CardButton {
    label?: string;
    title?: string;
    url?: string;
    target?: string;
}

interface CardImage {
    src?: string;
    alt?: string;
    altText?: string;
}

interface Card {
    headline?: string;
    copy?: string;
    image?: CardImage;
    button?: CardButton;
}

const props = defineProps<{
    headline?: string;
    copy?: string;
    cards?: {
        first_card?: Card;
        second_card?: Card;
    };
}>();

const shardClasses = ['shard-clip-1', 'shard-clip-3'];

const cardList = computed(() => {
    const list: Card[] = [];
    if (props.cards?.first_card) list.push(props.cards.first_card);
    if (props.cards?.second_card) list.push(props.cards.second_card);
    return list;
});

const cardsContainer = ref(null);
onMounted(() => {
    if (!cardsContainer.value) return;
    const cards = cardsContainer.value.querySelectorAll('.card-animate');
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('card-visible');
                observer.unobserve(entry.target);
            }
        });
    }, { threshold: 0.25 });

    cards.forEach((el) => observer.observe(el));
});
</script>

<template>
    <section class="block-manual-entry-two-up text-white py-[60px] px-[var(--box-padding-lr-m)] lg:py-[90px] lg:px-0" tabindex="-1">

        <div class="max-w-[1360px] w-full lg:w-[calc(1360/1440*100vw)] mx-auto flex flex-col gap-10 lg:gap-[72px]">

            <!-- Header (optional) -->
            <div v-if="headline || copy" class="flex flex-col gap-[18px] lg:max-w-[819px]">
                <MessHtml v-if="headline" tag="h2" :html="stripBlockTags(headline)"
                    class="font-hubspot-serif font-normal
                        text-[calc(42/var(--base-fs)*1rem)] leading-[1.1] tracking-normal
                        lg:text-[calc(60/var(--base-fs)*1rem)]" />
                <MessHtml v-if="copy" :html="copy"
                    class="font-hubspot-sans font-light
                        text-[calc(16/var(--base-fs)*1rem)] leading-[26px] tracking-[0.32px]" />
            </div>

            <!-- Cards -->
            <div ref="cardsContainer" class="flex flex-col gap-10 lg:flex-row lg:gap-[67px] lg:items-stretch">
                <div v-for="(card, index) in cardList"
                    :key="`${card.headline}-${index}`"
                    class="card-animate flex flex-col gap-[29px] lg:flex-1">

                    <div v-if="card.image" class="aspect-[409/277] relative"
                        :class="shardClasses[index]">
                        <MessImage :image="card.image" class="size-full object-cover" />
                        <div class="shard-overlay absolute inset-0" aria-hidden="true"></div>
                    </div>

                    <div class="flex flex-col gap-[26px] grow">
                        <div class="flex flex-col gap-3">
                            <MessHtml v-if="card.headline" tag="h3" :html="stripBlockTags(card.headline)"
                                class="font-hubspot-serif font-normal
                                    text-[calc(30/var(--base-fs)*1rem)] leading-[1.3] tracking-normal" />
                            <MessHtml v-if="card.copy" :html="card.copy"
                                class="font-hubspot-sans font-light
                                    text-[calc(15/var(--base-fs)*1rem)] leading-[23px] tracking-[0.3px]" />
                        </div>
                        <MessLink v-if="card.button?.label"
                            :href="card.button.url || '#'"
                            class="inline-flex self-start items-center gap-2.5
                                px-5 py-2.5 rounded-[50px]
                                border border-white bg-transparent text-white no-underline
                                font-hubspot-serif font-normal text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px]
                                transition-colors duration-200
                                hover:bg-[var(--orange)] hover:border-[var(--orange)] hover:text-white
                                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2">
                            {{ card.button.label }}
                        </MessLink>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style>
.block-manual-entry-two-up {
    background: linear-gradient(147deg, rgb(84, 7, 72) 23.5%, var(--purple, #820CA7) 58.7%, rgb(150, 22, 127) 79.8%);
}

.block-manual-entry-two-up .shard-clip-1 {
    clip-path: url(#shard-1);
}
.block-manual-entry-two-up .shard-clip-3 {
    clip-path: url(#shard-3);
}

.block-manual-entry-two-up .shard-overlay {
    background: linear-gradient(to bottom, rgba(255, 72, 0, 0.1) 0%, rgba(130, 12, 167, 0.5) 100%);
}

.block-manual-entry-two-up .mess-html a {
    color: white;
    text-decoration-line: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
    text-decoration-thickness: 1px;
    transition: text-decoration 0.2s ease;
}

@media (hover: hover) {
    .block-manual-entry-two-up .mess-html a:hover {
        text-decoration: none;
    }
}

.card-animate {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.6s ease, transform 0.6s ease;
}

.card-animate:nth-child(2) {
    transition-delay: 0.15s;
}

.card-animate.card-visible {
    opacity: 1;
    transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
    .card-animate {
        opacity: 1;
        transform: none;
        transition: none;
    }
}
</style>
